import dayjs, { type Dayjs } from 'dayjs';
import type {
  AppAdminTenancySchemasContractContractOut as ContractOut,
  OrganizationPublisherOut,
} from '@/api/generated/schemas';

/** Why the backend would refuse to enable the publisher (`type` of a refusal). */
export type PublisherEnableViolation = 'no_organization' | 'no_contract';

/** One day the enable rule looks at: where the publisher is and what covers it. */
export interface PublisherCoverageCheck {
  /** `YYYY-MM-DD` — today, or the start of a scheduled move. */
  day: string;
  /** The publisher's link in force that day. */
  link: OrganizationPublisherOut;
  /** The contract covering it that day; `null` = uncovered. */
  contract: ContractOut | null;
}

export interface PublisherStanding {
  /** Link in force today; `null` = in no organization. */
  current: OrganizationPublisherOut | null;
  /** Links that have not started yet, earliest first — scheduled moves. */
  scheduled: OrganizationPublisherOut[];
  /** Today (when in an organization) and the start of every scheduled move. */
  checks: PublisherCoverageCheck[];
  /** Empty = the backend would accept an enable today. */
  violations: PublisherEnableViolation[];
}

/** The link in force on `day`: the latest started — the next one ends it. */
const linkOn = (
  sortedLinks: OrganizationPublisherOut[],
  day: string
): OrganizationPublisherOut | null =>
  sortedLinks.reduce<OrganizationPublisherOut | null>(
    (inForce, link) => (link.valid_from <= day ? link : inForce),
    null
  );

/**
 * The contract covering the publisher in `organizationId` on `day`: one of
 * that organization's contracts, for this publisher or organization-wide
 * (`publisher_id` null), started by then. A dedicated contract is shown in
 * preference to the organization's default, the newest first.
 */
const contractOn = (
  contracts: ContractOut[],
  publisherId: number,
  organizationId: number,
  day: string
): ContractOut | null =>
  contracts
    .filter(
      (c) =>
        c.organization_id === organizationId &&
        (c.publisher_id === publisherId || c.publisher_id === null) &&
        c.valid_from <= day
    )
    .sort(
      (a, b) =>
        Number(b.publisher_id !== null) - Number(a.publisher_id !== null) ||
        b.valid_from.localeCompare(a.valid_from)
    )[0] ?? null;

/**
 * Whether the publisher may be enabled, and why not — mirrors the backend's
 * rule (`PublisherService._mod_status_refusals`):
 *
 * - `no_organization`: no link in force today. A link has no end; the
 *   publisher's next link ends it, so the one in force is the latest
 *   started.
 * - `no_contract`: not covered today, or on the start of any scheduled move.
 *   Neither links nor contracts end, so within one stay coverage only grows
 *   and those days decide it. Without an organization today the publisher
 *   is uncovered too — the backend reports both.
 *
 * `links` must hold every link of the publisher, in any organization;
 * `contracts` those of the organizations the links point to. The backend
 * stays the authority — this only explains a refusal before it happens.
 */
export const resolvePublisherStanding = (
  publisherId: number,
  links: OrganizationPublisherOut[],
  contracts: ContractOut[],
  today: Dayjs = dayjs()
): PublisherStanding => {
  const own = links
    .filter((link) => link.publisher_id === publisherId)
    .sort((a, b) => a.valid_from.localeCompare(b.valid_from));
  const day = today.format('YYYY-MM-DD');
  const current = linkOn(own, day);
  const scheduled = own.filter((link) => link.valid_from > day);

  const checks = [day, ...scheduled.map((link) => link.valid_from)].flatMap(
    (checkDay): PublisherCoverageCheck[] => {
      const link = linkOn(own, checkDay);
      if (!link) return [];
      return [
        {
          day: checkDay,
          link,
          contract: contractOn(
            contracts,
            publisherId,
            link.organization_id,
            checkDay
          ),
        },
      ];
    }
  );

  const violations: PublisherEnableViolation[] = [];
  if (!current) violations.push('no_organization');
  if (!current || checks.some((check) => !check.contract)) {
    violations.push('no_contract');
  }
  return { current, scheduled, checks, violations };
};
